import { Box, Grid, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Delete, Edit, History } from '@mui/icons-material';
import useDebounce from '../../../hooks/useDebounce';
import { useUserProjects } from '../../../query/apiQueries';
import { projectTableHead } from '../../../static-data/projects';
import SearchBar from '../../../ui/primitives/SearchBar';
import TableComponent from '../../../ui/data/table/TableComponent';
import EditUserProject from './EditUserProject';
import FavouriteProjectToggle from './FavouriteProjectToggle';
import ProjectTableAction from './ProjectTableAction';
import ActionMenu from '../../../ui/primitives/ActionMenu';
import { useAccess } from '../../../hooks/useAccess';

const UserProjectSection = () => {
  const navigate = useNavigate();
  const { canAccess } = useAccess();
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState('desc');
  const [searchValue, setSearchValue] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const debouncedSearchQuery = useDebounce(searchValue, 500);

  const {
    data: projectData,
    isLoading,
    refetch,
  } = useUserProjects({
    perPage,
    page,
    search: debouncedSearchQuery,
    includeTestCases: false,
    includeLinkedProjects: true,
  });

  const projects = projectData?.projects || [];
  const totalCount = projectData?.total || 0;

  const handlePageSizeChange = (e) => {
    setPerPage(parseInt(e.target.value, 10));
    setPage(1);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}>
        <Typography variant="h4">Projects</Typography>
      </Box>

      {/* Search bar */}
      <Grid container spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Grid item xs={12} sm>
        <SearchBar
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          clearSearch={() => setSearchValue('')}
          placeholder="Search Projects"
        />
        </Grid>
        <ProjectTableAction fetchProjectData={refetch} />
      </Grid>

      {/* Table */}
      <TableComponent
        data={projects}
        headerTabs={projectTableHead}
        page={page}
        setPage={setPage}
        perPage={perPage}
        order={order}
        setOrder={setOrder}
        defaultSort="created_at"
        isLoading={isLoading}
        striped
        totalCount={totalCount}
        showPages
        handlePageChange={setPage}
        tablekey="projects"
        showCount
        showRowCount
        setPerPage={handlePageSizeChange}
        onRefresh={refetch}
        action={(row) => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <FavouriteProjectToggle
              projectId={row.id}
              isMarkedFavourite={row.is_favourite}
              refetch={refetch}
            />
            <ActionMenu
              items={[
                canAccess('ManageProjects') && {
                  label: 'Edit',
                  icon: <Edit />,
                  onClick: () => setEditRow(row),
                },
                {
                  label: 'View History',
                  icon: <History />,
                  onClick: () => navigate(`/project/${row.id}/history`),
                },
                canAccess('DeleteProjects') && {
                  label: 'Delete',
                  icon: <Delete />,
                  onClick: () => setDeleteRow(row),
                  color: 'error',
                },
              ].filter(Boolean)}
            />
          </Stack>
        )}
      />

      {(editRow || deleteRow) && (
        <EditUserProject
          selectedProject={editRow || deleteRow}
          fetchProjectData={refetch}
          controlled
          open={!!editRow}
          onClose={() => setEditRow(null)}
          deleteOpen={!!deleteRow}
          onDeleteClose={() => setDeleteRow(null)}
        />
      )}
    </Box>
  );
};

export default UserProjectSection;
